import { TextInput, useStyles } from '@archipro-design/aria';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as S from '@modules/product-tagging/components/product-tagging-search/ProductTaggingSearchModal.style';
import {
    TaggingModalShell,
    TaggingModalStaticHeader,
    TaggingModalFieldUnderline,
    TaggingModalContent,
    TaggingModalFooterButton,
} from '@modules/product-tagging/components/tagging-modal-shell/TaggingModalShell';
import {
    ensureProtocol,
    normaliseURL,
    isValidURL,
    hasValidTLD,
} from '~/modules/url/utils/url';

/** Matches the Varchar(255) DB column; URLs are truncated to this length before submission. */
const MAX_URL_DB_LENGTH = 255;

export interface TaggingFormFieldDescriptor {
    /** Unique key used as React key and to index into the values record. */
    name: string;
    placeholder: string;
    maxLength?: number;
    autoFocus?: boolean;
    /**
     * When true the field is treated as a URL: blank-check + protocol normalisation
     * + `isValidURL` are applied instead of a simple non-empty check.
     */
    isURL?: boolean;
}

interface TaggingModalFormProps<T> {
    title: string;
    message: React.ReactNode;
    fields: TaggingFormFieldDescriptor[];
    /** Transform normalised field values into the payload passed to `onSubmit`. */
    buildPayload: (values: Record<string, string>) => T;
    onSubmit: (payload: T) => void;
    onDismiss: () => void;
    formId?: string;
}

function isFieldValid(
    value: string,
    field: TaggingFormFieldDescriptor,
): boolean {
    if (field.isURL) {
        const trimmed = value.trim();
        if (trimmed.length === 0) return false;
        const withProtocol = ensureProtocol(trimmed);
        if (!isValidURL(withProtocol)) return false;
        try {
            return hasValidTLD(new URL(withProtocol).hostname);
        } catch {
            return false;
        }
    }
    return value.trim().length > 0;
}

/**
 * Normalise the raw field value before it leaves the form. URL fields get
 * protocol-ensured and truncated to the DB column width; text fields are trimmed.
 */
function normaliseFieldValue(
    value: string,
    field: TaggingFormFieldDescriptor,
): string {
    if (field.isURL) {
        return normaliseURL(value).slice(0, MAX_URL_DB_LENGTH);
    }
    return value.trim();
}

/**
 * Declarative form modal for product-tagging flows. Accepts a `fields` array
 * describing each input and a `buildPayload` function that converts the
 * normalised values into a `TagDataUseToCreateTag` (or any other shape the
 * parent expects).
 *
 * Handles state, validation (non-empty + URL format), submission guard, and the
 * standard TaggingModalShell / StaticHeader / Content / FooterButton layout.
 */
function TaggingModalForm<T>({
    title,
    message,
    fields,
    buildPayload,
    onSubmit,
    onDismiss,
    formId,
}: TaggingModalFormProps<T>) {
    const { css } = useStyles();

    const initialValues = Object.fromEntries(fields.map((f) => [f.name, '']));
    const [values, setValues] = useState<Record<string, string>>(initialValues);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const allValid = fields.every((f) => isFieldValid(values[f.name] ?? '', f));
    const canSubmit = allValid && !isSubmitting;

    const handleChange = useCallback(
        (name: string, value: string) =>
            setValues((prev) => ({ ...prev, [name]: value })),
        [],
    );

    const handleSubmit = useCallback(() => {
        if (!canSubmit) return;
        setIsSubmitting(true);

        const normalised = Object.fromEntries(
            fields.map((f) => [
                f.name,
                normaliseFieldValue(values[f.name] ?? '', f),
            ]),
        );

        try {
            onSubmit(buildPayload(normalised));
        } catch {
            if (mountedRef.current) setIsSubmitting(false);
        }
    }, [canSubmit, fields, values, buildPayload, onSubmit]);

    const onFormSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            handleSubmit();
        },
        [handleSubmit],
    );

    return (
        <TaggingModalShell onDismiss={onDismiss}>
            <TaggingModalStaticHeader
                title={title}
                message={message}
                onClose={onDismiss}
            />

            <form
                id={formId}
                onSubmit={onFormSubmit}
                className="flex min-h-0 flex-1 flex-col"
            >
                <TaggingModalContent className="px-4 pb-4">
                    <div className="flex flex-col gap-4">
                        {fields.map((field) => (
                            <div key={field.name} className="w-full">
                                <TextInput
                                    className={css(S.SearchTextInput)}
                                    input={{
                                        value: values[field.name] ?? '',
                                        placeholder: field.placeholder,
                                        autoFocus: field.autoFocus,
                                        maxLength: field.maxLength,
                                    }}
                                    size="md"
                                    onChange={(
                                        e: React.SyntheticEvent<HTMLElement>,
                                    ) =>
                                        handleChange(
                                            field.name,
                                            (e.target as HTMLInputElement)
                                                .value,
                                        )
                                    }
                                />
                                <TaggingModalFieldUnderline />
                            </div>
                        ))}
                    </div>
                </TaggingModalContent>

                <TaggingModalFooterButton
                    label="Submit"
                    type="submit"
                    disabled={!canSubmit}
                    loading={isSubmitting}
                />
            </form>
        </TaggingModalShell>
    );
}

export default TaggingModalForm;
